import { DataType } from 'src/app/data-types/models/data-types.interface';

/** Built-in classification framework id for Secrets/Credentials data types (always top-sensitivity). */
const SECRETS_CREDENTIALS_FRAMEWORK = 'secretsCredentials';

/**
 * Built-in compliance framework id → sensitivity score (1–5). Secrets/Credentials tops
 * the scale; any custom framework also scores 5 (handled in `scoreDataType`).
 */
const FRAMEWORK_SENSITIVITY: Readonly<Record<string, number>> = {
    [SECRETS_CREDENTIALS_FRAMEWORK]: 5,
    pci: 4,
    phi: 3,
    gdprSpecial: 2,
    cpraSpi: 2, // CPRA Sensitive Personal Information — scored at the GDPR-special tier
    pii: 1,
};

/** Floor score for a data type with no recognised framework (treated as PII-level). */
const NO_FRAMEWORK_SCORE = 1;

/** Top score for any custom (company-defined) framework. */
const TOP_SCORE = 5;

/** Per-data-type score (0–5) plus a human-readable reason (used for debug logging). */
export interface DataTypeScore {
    id: string;
    name: string;
    score: number;
    reason: string;
}

/** Resolves a framework id to a display label for human-readable reasons (debug only). */
export type FrameworkLabel = (id: string) => string;
const identityLabel: FrameworkLabel = id => id;

/** Per-data-type sensitivity score (0–5) with the rule that produced it. */
function scoreDataType(dataType: DataType, customFrameworkIds: Set<string>, frameworkLabel: FrameworkLabel = identityLabel): DataTypeScore {
    if (!dataType) {
        return { id: '(unknown)', name: '(unresolved)', score: 0, reason: 'unresolved data type → 0' };
    }
    const name = dataType.name || dataType.id;
    const frameworkIds = (dataType.frameworks ?? []).map(framework => framework?.id).filter(Boolean) as string[];
    const customMatches = frameworkIds.filter(id => customFrameworkIds.has(id));
    if (customMatches.length) {
        return { id: dataType.id, name, score: TOP_SCORE, reason: `custom framework(s) [${customMatches.map(frameworkLabel).join(', ')}] → ${TOP_SCORE}` };
    }
    const ranked = frameworkIds
        .map(id => ({ id, score: FRAMEWORK_SENSITIVITY[id] }))
        .filter((entry): entry is { id: string; score: number } => entry.score !== undefined);
    if (!ranked.length) {
        const reason = frameworkIds.length ? `unrecognised framework(s) [${frameworkIds.map(frameworkLabel).join(', ')}] → ${NO_FRAMEWORK_SCORE}` : `no frameworks → ${NO_FRAMEWORK_SCORE}`;
        return { id: dataType.id, name, score: NO_FRAMEWORK_SCORE, reason };
    }
    const top = ranked.reduce((best, entry) => (entry.score > best.score ? entry : best));
    const matched = ranked.map(entry => `${frameworkLabel(entry.id)}→${entry.score}`).join(', ');
    // List ALL matched frameworks (not just the winner) so co-tagged ones — e.g. cpraSpi, which
    // ties gdprSpecial and is dominated by pci/phi/secretsCredentials — stay visible in the breakdown.
    const reason = ranked.length > 1
        ? `framework '${frameworkLabel(top.id)}' → ${top.score} [matched: ${matched}]`
        : `framework '${frameworkLabel(top.id)}' → ${top.score}`;
    return { id: dataType.id, name, score: top.score, reason };
}

/** Per-data-type sensitivity score (0–5). */
function resolveDataTypeScore(dataType: DataType, customFrameworkIds: Set<string>): number {
    return scoreDataType(dataType, customFrameworkIds).score;
}

/**
 * Framework-based data sensitivity for a system, scored as the maximum across all its
 * (resolved, accepted) data types. Pure — no Angular dependencies. Returns 0 when the
 * system has no resolvable data types.
 *
 * @param dataTypes catalog data types on the system (resolved via DataTypesService.getExtendedDataTypes)
 * @param customFrameworkIds ids of company-defined (custom) frameworks — any of these scores 5
 */
export function resolveSystemDataSensitivityScore(dataTypes: DataType[], customFrameworkIds: Set<string>): number {
    if (!dataTypes?.length) {
        return 0;
    }
    return Math.max(...dataTypes.map(dataType => resolveDataTypeScore(dataType, customFrameworkIds)));
}

/** Debug breakdown: the system score plus a per-data-type score+reason list. */
export interface SystemDataSensitivityExplanation {
    score: number;
    breakdown: DataTypeScore[];
}

/** Same scoring as `resolveSystemDataSensitivityScore`, but returns the per-data-type
 *  reasoning for debugging/logging. */
export function explainSystemDataSensitivity(dataTypes: DataType[], customFrameworkIds: Set<string>, frameworkLabel?: FrameworkLabel): SystemDataSensitivityExplanation {
    const breakdown = (dataTypes ?? []).map(dataType => scoreDataType(dataType, customFrameworkIds, frameworkLabel));
    const score = breakdown.length ? Math.max(...breakdown.map(entry => entry.score)) : 0;
    return { score, breakdown };
}
